import React, { useMemo, useState } from 'react';

export default function RecipeIngredientMultiPicker({
    options = [],
    loading = false,
    disabled = false,
    onAddSelected,
    onCancel,
    searchPlaceholder = 'Search inventory items…',
    emptyMessage = 'No inventory items found',
    quantityPlaceholder = 'Quantity',
}) {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [quantityDrafts, setQuantityDrafts] = useState({});

    const searchQuery = search.trim().toLowerCase();

    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        return options.filter((opt) =>
            String(opt.label ?? '')
                .toLowerCase()
                .includes(searchQuery)
        );
    }, [options, searchQuery]);

    const selectedCount = selectedIds.size;

    const toggleOption = (value) => {
        const id = String(value);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const updateQuantityDraft = (value, qty) => {
        const id = String(value);
        setQuantityDrafts((prev) => ({ ...prev, [id]: qty }));
    };

    const handleAddSelected = () => {
        if (selectedCount === 0 || typeof onAddSelected !== 'function') return;
        const items = [...selectedIds]
            .map((id) => ({
                inventoryItemId: Number(id),
                quantity: quantityDrafts[id] ?? '',
            }))
            .filter((item) => item.inventoryItemId);
        onAddSelected(items);
        setSelectedIds(new Set());
        setQuantityDrafts((prev) => {
            const next = { ...prev };
            items.forEach((item) => {
                delete next[String(item.inventoryItemId)];
            });
            return next;
        });
        setSearch('');
    };

    const handleCancel = () => {
        setSelectedIds(new Set());
        setQuantityDrafts({});
        setSearch('');
        if (typeof onCancel === 'function') {
            onCancel();
        }
    };

    return (
        <div className="recipe-ingredient-multi-picker">
            <div className="recipe-ingredient-multi-picker-toolbar">
                <input
                    type="text"
                    className="add-menu-form-field recipe-ingredient-multi-picker-search"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={disabled || loading}
                />
                <div className="recipe-ingredient-multi-picker-actions">
                    <button
                        type="button"
                        className="recipe-ingredient-multi-picker-btn recipe-ingredient-multi-picker-btn--cancel"
                        onClick={handleCancel}
                        disabled={disabled || loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="add-button recipe-ingredient-multi-picker-btn"
                        onClick={handleAddSelected}
                        disabled={disabled || loading || selectedCount === 0}
                    >
                        Add selected ({selectedCount})
                    </button>
                </div>
            </div>
            <div className="recipe-ingredient-multi-picker-list-wrap">
                {loading ? (
                    <p className="recipe-ingredient-multi-picker-empty">Loading inventory…</p>
                ) : filteredOptions.length === 0 ? (
                    <p className="recipe-ingredient-multi-picker-empty">
                        {options.length === 0 ? emptyMessage : 'No items match your search.'}
                    </p>
                ) : (
                    <ul className="recipe-ingredient-multi-picker-list">
                        {filteredOptions.map((opt) => {
                            const id = String(opt.value);
                            const checked = selectedIds.has(id);
                            return (
                                <li key={id} className="recipe-ingredient-multi-picker-list-item">
                                    <div className="recipe-ingredient-multi-picker-row">
                                        <label className="recipe-ingredient-multi-picker-option">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleOption(opt.value)}
                                                disabled={disabled}
                                            />
                                            <span
                                                className={
                                                    checked
                                                        ? 'recipe-ingredient-multi-picker-name recipe-ingredient-multi-picker-name--selected'
                                                        : 'recipe-ingredient-multi-picker-name'
                                                }
                                            >
                                                {opt.label}
                                            </span>
                                        </label>
                                        <div className="inventory-input-with-addon recipe-ingredient-multi-picker-qty-wrap">
                                            <input
                                                type="number"
                                                className="add-menu-form-field recipe-ingredient-multi-picker-qty inventory-input--has-suffix"
                                                value={quantityDrafts[id] ?? ''}
                                                onChange={(e) =>
                                                    updateQuantityDraft(opt.value, e.target.value)
                                                }
                                                placeholder={quantityPlaceholder}
                                                step="any"
                                                min="0"
                                                disabled={disabled}
                                                aria-label={`Quantity for ${opt.label}`}
                                            />
                                            <span
                                                className="inventory-input-addon inventory-input-addon--suffix"
                                                aria-hidden="true"
                                            >
                                                {opt.unitSymbol || 'unit'}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
